/**
 * DisciplineDetailSheet — the unified Detailed Estimation sheet.
 *
 * ONE config-driven component for all 12 disciplines (replaces the old
 * StructuralDetailView / MechanicalDetailView / MrDetailView clones).
 * Column groups, per-discipline visibility, and labour-hour labels all come
 * from the shared config served by GET /api/cost-breakdown/config.
 *
 * Rows live in cb_discipline_detail scoped to the selected core estimate.
 * Calculated columns (marked ⚡) are recomputed server-side on every save.
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useCbApi, useCbConfig } from './cbApi';
import {
  SheetShell, HeaderButton, ActionButton, EditableTable, HelpModal,
  LoadingState, ErrorState, EmptyState, NoEstimateState, useToast, downloadExport, fmt,
} from './CbUi';
import CbAuditLogButton from './CbAuditLogButton';

const DisciplineDetailSheet = ({ estimate, discipline, onOpenSummary }) => {
  const api = useCbApi();
  const { config, loading: configLoading } = useCbConfig();
  const { showToast, ToastEl } = useToast();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [showHelp, setShowHelp] = useState(false);
  const [activeGroup, setActiveGroup] = useState('ALL');
  const [rollingUp, setRollingUp] = useState(false);
  const [pullingMto, setPullingMto] = useState(false);

  const disc = useMemo(
    () => config?.disciplines.find((d) => d.key === discipline) || { label: discipline, code: '?', color: '#6b7280', icon: '📋' },
    [config, discipline]
  );

  // Column groups visible for this discipline, with label overrides applied
  const columnGroups = useMemo(() => {
    if (!config) return [];
    const overrides = config.detailOverrides?.[discipline] || {};
    return config.detailColumnGroups
      .map((group) => ({
        ...group,
        columns: group.columns
          .filter((col) => !col.disciplines || col.disciplines.includes(discipline))
          .map((col) => (overrides[col.key] ? { ...col, label: overrides[col.key] } : col)),
      }))
      .filter((group) => group.columns.length > 0);
  }, [config, discipline]);

  const visibleGroups = useMemo(
    () => (activeGroup === 'ALL' ? columnGroups : columnGroups.filter((g) => g.key === activeGroup || g.key === 'lgl' || g.key === 'client')),
    [columnGroups, activeGroup]
  );
  const flatColumns = useMemo(() => visibleGroups.flatMap((g) => g.columns), [visibleGroups]);
  const groupedHeader = useMemo(
    () => visibleGroups.map((g) => ({ label: g.label, color: g.color, span: g.columns.length })),
    [visibleGroups]
  );

  const fetchRows = useCallback(async () => {
    if (!estimate?.id) return;
    try {
      setLoading(true);
      const res = await api.get(`/cost-breakdown/discipline-detail?estimate_id=${estimate.id}&discipline=${encodeURIComponent(discipline)}`);
      setRows(res.data?.data || []);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [api, estimate?.id, discipline]);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  const filteredRows = useMemo(() => {
    if (!search) return rows;
    const term = search.toLowerCase();
    return rows.filter((r) =>
      ['tag', 'description', 'area', 'account_no', 'wbs_code', 'sov_code', 'notes'].some(
        (k) => String(r[k] ?? '').toLowerCase().includes(term)
      )
    );
  }, [rows, search]);

  const kpis = useMemo(() => {
    const sum = (key) => rows.reduce((s, r) => s + (Number(r[key]) || 0), 0);
    return [
      { label: 'Scope Items', value: rows.length, icon: '📋', color: disc.color },
      { label: 'Factored Hours', value: fmt(sum('factored_total_comb_hours'), 'number'), icon: '⏱️', color: '#059669' },
      { label: 'Labour Cost', value: fmt(sum('total_comb_labour_cost'), 'currency'), icon: '💰', color: '#dc2626' },
      { label: 'Material Cost', value: fmt(sum('total_material_cost'), 'currency'), icon: '📦', color: '#d97706' },
      { label: 'Subs Cost', value: fmt(sum('total_sub_cost'), 'currency'), icon: '🤝', color: '#be185d' },
    ];
  }, [rows, disc.color]);

  const handleCellSave = useCallback(async (row, key, value) => {
    try {
      const res = await api.put(`/cost-breakdown/discipline-detail/${row.id}`, { [key]: value });
      const updated = res.data?.data;
      if (updated) setRows((prev) => prev.map((r) => (r.id === row.id ? updated : r)));
      else fetchRows();
    } catch (err) {
      showToast(`Update failed: ${err.message}`, 'error');
    }
  }, [api, fetchRows, showToast]);

  const handleAddRow = useCallback(async () => {
    try {
      const res = await api.post('/cost-breakdown/discipline-detail', {
        estimate_id: estimate.id,
        discipline,
        description: 'New scope item',
        sort_order: (rows.length + 1) * 10,
      });
      const created = res.data?.data;
      if (created) setRows((prev) => [...prev, created]);
      showToast('Scope item added — click any cell to edit');
    } catch (err) {
      showToast(`Failed to add row: ${err.message}`, 'error');
    }
  }, [api, estimate?.id, discipline, rows.length, showToast]);

  const handleBulkDelete = useCallback(async () => {
    if (selectedRows.size === 0) return;
    if (!window.confirm(`Delete ${selectedRows.size} selected scope item(s)?`)) return;
    try {
      await api.post('/cost-breakdown/discipline-detail/bulk-delete', { ids: [...selectedRows] });
      setRows((prev) => prev.filter((r) => !selectedRows.has(r.id)));
      setSelectedRows(new Set());
      showToast('Deleted');
    } catch (err) {
      showToast(`Delete failed: ${err.message}`, 'error');
    }
  }, [api, selectedRows, showToast]);

  const handleExport = useCallback(async () => {
    try {
      await downloadExport(api, `/cost-breakdown/discipline-detail/export?estimate_id=${estimate.id}&discipline=${encodeURIComponent(discipline)}`,
        `${estimate.estimate_number}_${discipline}_detail.xlsx`);
      showToast('Exported');
    } catch (err) {
      showToast(`Export failed: ${err.message}`, 'error');
    }
  }, [api, estimate, discipline, showToast]);

  const handleRollUp = useCallback(async () => {
    setRollingUp(true);
    try {
      const res = await api.post('/cost-breakdown/discipline-summary/pull-from-detail', {
        estimate_id: estimate.id,
        discipline,
      });
      showToast(res.data?.message || 'Rolled up into Discipline Summary');
    } catch (err) {
      showToast(`Roll-up failed: ${err.response?.data?.message || err.message}`, 'error');
    } finally {
      setRollingUp(false);
    }
  }, [api, estimate?.id, discipline, showToast]);

  const handlePullMto = useCallback(async () => {
    setPullingMto(true);
    try {
      const res = await api.post('/cost-breakdown/pull-from-mto', {
        estimate_id: estimate.id,
        discipline,
      });
      showToast(res.data?.message || 'Pulled from MTO');
      fetchRows();
    } catch (err) {
      showToast(err.response?.data?.message || err.message, 'error');
    } finally {
      setPullingMto(false);
    }
  }, [api, estimate?.id, discipline, fetchRows, showToast]);

  if (!estimate?.id) return <NoEstimateState section="Detailed Estimation" />;
  if (configLoading || (loading && rows.length === 0)) return <LoadingState label={`Loading ${disc.label} detail…`} />;
  if (error) return <ErrorState error={error} onRetry={fetchRows} />;

  return (
    <>
      <SheetShell
        title={`${disc.icon} ${disc.label} — Detailed Estimation`}
        subtitle={`Estimate ${estimate.estimate_number} · ${estimate.estimate_name || ''} · scope-item level takeoff`}
        gradient={`linear-gradient(135deg, ${disc.color}, ${disc.color}cc)`}
        headerActions={
          <>
            {(discipline === 'piping' || discipline === 'pipe_fab') && (
              <HeaderButton onClick={handlePullMto} title="Pull hours and material from Piping MTO">
                {pullingMto ? '⏳ Pulling MTO…' : '📋 Pull from MTO'}
              </HeaderButton>
            )}
            <HeaderButton onClick={handleRollUp} title="Summarize this sheet into the Discipline Summary">
              {rollingUp ? '⏳ Rolling up…' : '⬆️ Roll up to Summary'}
            </HeaderButton>
            {onOpenSummary && <HeaderButton onClick={() => onOpenSummary(discipline)}>📊 Open Summary</HeaderButton>}
            <HeaderButton onClick={handleExport}>📥 Export</HeaderButton>
            <CbAuditLogButton
              tableName="cb_discipline_detail"
              estimateId={estimate?.id}
              title={`${disc.label} Detail Audit Log`}
              accentColor={disc.color}
            />
            <HeaderButton onClick={() => setShowHelp(true)}>❓ Help</HeaderButton>
          </>
        }
        kpiCards={kpis}
        toolbar={
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
              <input
                placeholder="Search tags, descriptions, areas, codes…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                style={{ flex: '1 1 240px', maxWidth: 380, padding: '9px 14px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 13, background: '#fff' }}
              />
              <ActionButton color={disc.color} onClick={handleAddRow}>+ Add Scope Item</ActionButton>
              {selectedRows.size > 0 && (
                <ActionButton color="#dc2626" onClick={handleBulkDelete}>🗑️ Delete ({selectedRows.size})</ActionButton>
              )}
            </div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {[{ key: 'ALL', label: 'All Columns', color: disc.color }, ...columnGroups.filter((g) => !['lgl', 'client'].includes(g.key))].map((g) => (
                <button
                  key={g.key}
                  onClick={() => setActiveGroup(g.key)}
                  style={{
                    padding: '5px 12px', borderRadius: 20, fontSize: 11.5, fontWeight: 600,
                    border: activeGroup === g.key ? `2px solid ${g.color}` : '1px solid #d1d5db',
                    background: activeGroup === g.key ? g.color : '#fff',
                    color: activeGroup === g.key ? '#fff' : '#4b5563', cursor: 'pointer',
                  }}
                >
                  {g.label}
                </button>
              ))}
            </div>
          </div>
        }
      >
        {rows.length === 0 ? (
          <EmptyState icon={disc.icon} title={`No ${disc.label} scope items yet`}>
            <p>Click <strong>+ Add Scope Item</strong> to start the detailed takeoff for this discipline.</p>
            <p>When the sheet is populated, use <strong>Roll up to Summary</strong> to feed the Discipline Summary and the CB one-pager.</p>
          </EmptyState>
        ) : (
          <EditableTable
            columns={flatColumns}
            rows={filteredRows}
            onCellSave={handleCellSave}
            selectedRows={selectedRows}
            onToggleRow={(id) => setSelectedRows((prev) => {
              const next = new Set(prev);
              next.has(id) ? next.delete(id) : next.add(id);
              return next;
            })}
            onToggleAll={() => setSelectedRows((prev) =>
              prev.size === filteredRows.length ? new Set() : new Set(filteredRows.map((r) => r.id))
            )}
            accent={disc.color}
            groupedHeader={groupedHeader}
          />
        )}
        {ToastEl}
      </SheetShell>

      <HelpModal isOpen={showHelp} onClose={() => setShowHelp(false)} title={`${disc.label} Detailed Estimation Help`} accent={disc.color} guideId="cb-detail-help">
        <p><strong>This is the scope-item takeoff sheet</strong> for the <strong>{disc.label}</strong> discipline of estimate <strong>{estimate.estimate_number}</strong>. One row = one piece of scope (a tag, an equipment item, a steel assembly…).</p>
        <h4>How the numbers flow</h4>
        <ul style={{ paddingLeft: 20 }}>
          <li>You enter hours, material, rentals and sub costs per scope item here.</li>
          <li>Columns marked <strong style={{ color: '#7c3aed' }}>⚡</strong> are <strong>calculated automatically</strong> (totals, factored hours) — you can't edit them directly.</li>
          <li><strong>Roll up to Summary</strong> aggregates this whole sheet into the discipline's Labour Planning Summary (rows tagged <code>AUTO</code>).</li>
          <li>The summary in turn feeds the one-page <strong>CB Summary</strong> for the entire estimate.</li>
        </ul>
        <h4>Working the sheet</h4>
        <ul style={{ paddingLeft: 20 }}>
          <li><strong>Click</strong> any cell to edit; Enter saves, Escape cancels.</li>
          <li>Use the <strong>column group pills</strong> (Cranes, Material Cost, Labour Hours…) to focus the grid — LGL Data and From Client columns always stay visible.</li>
          <li>The <strong>Labour Hours Factor</strong> scales total hours (e.g. 110 = +10% difficulty allowance).</li>
          <li>Use checkboxes + <strong>Delete</strong> for bulk removal, and <strong>Export</strong> for an Excel copy.</li>
        </ul>
        <p>Re-running <strong>Roll up to Summary</strong> is safe: it updates the same AUTO rows rather than duplicating them.</p>
      </HelpModal>
    </>
  );
};

export default DisciplineDetailSheet;
